import Link from '@rezdy/components/Link';
import { ITextStyleProps } from '@rezdy/components/TextStyle';
import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { CONTACT_SUPPORT } from '../constants/auth';
import routes from '../constants/routes';
import routing from '../services/routing';

import Message from './Message';

export interface ErrorMessageProps extends ITextStyleProps {
    i18nKey: string;
}

const unknownI18nKey = 'errors.serverCodes.UKNOWN';

export function ErrorMessage({ i18nKey, ...props }: ErrorMessageProps) {
    const { i18n } = useTranslation();

    let namespacedI18nKey = `errors.serverCodes.${i18nKey}`;

    // Ensure the key exists
    if (!i18n.exists(namespacedI18nKey)) {
        // Fallback to the top level
        // eslint-disable-next-line no-console
        console.assert(i18n.exists(i18nKey), `Cannot find fallback translation for ${i18nKey}`);
        namespacedI18nKey = i18nKey;
    }

    let translatedI18n;

    switch (i18nKey) {
        case 'INVALID_CLIENT_ID':
        case 'INVALID_ENTITY':
        case 'INVALID_GRANT_TYPE':
        case 'INVALID_IDENTITY_PROVIDER':
        case 'INVALID_LOGOUT_URI':
        case 'INVALID_REDIRECT_URI':
        case 'INVALID_RESPONSE_CODE':
        case 'MISSING_AUTHORIZATION_CODE':
        case 'MISSING_CLIENT_ID':
        case 'MISSING_REDIRECT_URI':
        case 'MISSING_RESPONSE_CODE':
        case 'UNKNOWN':
            // Use the UNKNOWN key as all matching errors will be referencing this anyway.
            translatedI18n = (
                <Trans i18nKey={unknownI18nKey}>
                    Login failed <br />
                    <br /> There has been a problem logging into your account. Please contact the Rezdy Humans on our
                    support team for help. <br />
                    <br />
                    <Link href={CONTACT_SUPPORT}>Click here to raise a ticket</Link>
                </Trans>
            );
            break;
        case 'EXPIRED_VERIFICATION_CODE':
            translatedI18n = (
                <Trans i18nKey={namespacedI18nKey}>
                    Password reset failed <br />
                    <br />
                    The verification code is incorrect. <br />
                    <br />
                    <Link href={routing.link(routes.FORGOT_PASSWORD)}>Resend verification password</Link>
                </Trans>
            );
            break;
        case 'INVALID_ACCOUNT_CREATION':
            translatedI18n = (
                <Trans i18nKey={namespacedI18nKey}>
                    This email address has already been registered. You can login
                    <Link href={routing.link(routes.SIGN_IN)}>here</Link>
                </Trans>
            );
            break;
        case 'INVALID_ACCOUNT_EMAIL':
            translatedI18n = (
                <Trans i18nKey={namespacedI18nKey}>
                    This email address can not be found. You can sign up
                    <Link href={routing.link(routes.SIGN_UP)}>here</Link>
                </Trans>
            );
            break;
        case 'INVALID_AUTH_CODE':
            translatedI18n = (
                <Trans i18nKey={namespacedI18nKey}>
                    Password reset failed <br />
                    <br />
                    The verification code is incorrect. <br />
                    <br />
                    <Link href={routing.link(routes.FORGOT_PASSWORD)}>Resend verification code</Link>
                </Trans>
            );
            break;
        case 'INVALID_USERNAME_PASSWORD':
            translatedI18n = (
                <Trans i18nKey={namespacedI18nKey}>
                    Sorry, the email or password was not right.
                    <br />
                    <b>Please try again.</b>
                </Trans>
            );
            break;
        case 'MISSING_ACCOUNT_EMAIL':
            translatedI18n = (
                <Trans i18nKey={namespacedI18nKey}>
                    This email address can not be found. <br />
                    <br />
                    You can sign up <Link href={routing.link(routes.SIGN_UP)}>here</Link>
                </Trans>
            );
            break;
        case 'USER_LOCKED_OUT':
            translatedI18n = (
                <Trans i18nKey={namespacedI18nKey}>
                    Account locked
                    <br />
                    <br />
                    This account has been temporarily locked due to the high number of failed login attempts.
                </Trans>
            );
            break;
        default:
            translatedI18n = <Trans i18nKey={namespacedI18nKey}></Trans>;
            break;
    }

    return <Message {...props}>{translatedI18n}</Message>;
}
